import React from 'react';
import { MessageCircle } from 'lucide-react';
import styled, { keyframes } from 'styled-components';

// Keyframe animations
const float = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(5deg);
  }
`;

const floatReverse = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(20px) rotate(-5deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
`;

const slideIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px) scale(0.8);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const phoneRotate = keyframes`
  0%, 100% {
    transform: rotateY(0deg) rotateX(0deg);
  }
  25% {
    transform: rotateY(5deg) rotateX(2deg);
  }
  50% {
    transform: rotateY(0deg) rotateX(0deg);
  }
  75% {
    transform: rotateY(-5deg) rotateX(-2deg);
  }
`;

// Styled components
const SceneContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(236, 100, 53, 0.05) 0%, rgba(255, 140, 66, 0.05) 50%, rgba(255, 159, 64, 0.05) 100%);
`;

const PhoneMockup = styled.div`
  position: relative;
  width: 120px;
  height: 240px;
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border-radius: 24px;
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.3),
    inset 0 0 0 2px rgba(255, 255, 255, 0.1),
    0 0 40px rgba(236, 100, 53, 0.2);
  animation: ${phoneRotate} 8s ease-in-out infinite;
  z-index: 10;
  
  &::before {
    content: '';
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: #000;
    border-radius: 2px;
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 180px;
    background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
    border-radius: 16px;
    box-shadow: inset 0 0 20px rgba(236, 100, 53, 0.1);
  }
`;

const PhoneScreen = styled.div`
  position: absolute;
  top: 20px;
  left: 10px;
  width: 100px;
  height: 180px;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
  border-radius: 16px;
  overflow: hidden;
  z-index: 2;
`;

const ChatBubble = styled.div<{ $isSent: boolean; $delay: number; $position: { top: string; left: string } }>`
  position: absolute;
  top: ${props => props.$position.top};
  left: ${props => props.$position.left};
  background: ${props => props.$isSent 
    ? 'linear-gradient(135deg, #EC6435 0%, #FF8C42 100%)' 
    : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)'};
  color: ${props => props.$isSent ? '#ffffff' : '#1a1a1a'};
  padding: 8px 12px;
  border-radius: 18px;
  font-size: 10px;
  font-weight: 500;
  max-width: 70px;
  box-shadow: 
    0 4px 12px rgba(0, 0, 0, 0.15),
    0 0 20px ${props => props.$isSent ? 'rgba(236, 100, 53, 0.3)' : 'rgba(0, 0, 0, 0.1)'};
  animation: 
    ${float} ${props => 3 + props.$delay}s ease-in-out infinite,
    ${slideIn} 0.6s ease-out ${props => props.$delay}s both;
  z-index: ${props => props.$isSent ? 3 : 2};
  transform-origin: ${props => props.$isSent ? 'bottom right' : 'bottom left'};
  
  &::before {
    content: '';
    position: absolute;
    bottom: -4px;
    ${props => props.$isSent ? 'right: 8px;' : 'left: 8px;'}
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 8px solid ${props => props.$isSent ? '#EC6435' : '#ffffff'};
    transform: ${props => props.$isSent ? 'rotate(45deg)' : 'rotate(-45deg)'};
  }
`;

const FloatingHeart = styled.div<{ $delay: number; $position: { top: string; left: string; size: string } }>`
  position: absolute;
  top: ${props => props.$position.top};
  left: ${props => props.$position.left};
  width: ${props => props.$position.size};
  height: ${props => props.$position.size};
  font-size: ${props => props.$position.size};
  animation: 
    ${floatReverse} ${props => 4 + props.$delay}s ease-in-out infinite,
    ${pulse} ${props => 2 + props.$delay}s ease-in-out infinite;
  opacity: 0.6;
  z-index: 1;
  filter: drop-shadow(0 0 10px rgba(255, 99, 132, 0.5));
`;

const Particle = styled.div<{ $delay: number; $position: { top: string; left: string } }>`
  position: absolute;
  top: ${props => props.$position.top};
  left: ${props => props.$position.left};
  width: 4px;
  height: 4px;
  background: radial-gradient(circle, rgba(255, 159, 64, 0.8) 0%, transparent 70%);
  border-radius: 50%;
  animation: 
    ${float} ${props => 5 + props.$delay}s ease-in-out infinite,
    ${pulse} ${props => 3 + props.$delay}s ease-in-out infinite;
  opacity: 0.4;
  z-index: 0;
`;

const GlowEffect = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(236, 100, 53, 0.2) 0%, transparent 70%);
  border-radius: 50%;
  animation: ${pulse} 3s ease-in-out infinite;
  z-index: 0;
`;

export const ChatAnimatedScene: React.FC = () => {
  const chatMessages = [
    { text: "Hey! 👋", isSent: false, delay: 0, position: { top: '20px', left: '10px' } },
    { text: "Hi there!", isSent: true, delay: 0.3, position: { top: '50px', left: '40px' } },
    { text: "How are you?", isSent: false, delay: 0.6, position: { top: '80px', left: '10px' } },
    { text: "Great! You?", isSent: true, delay: 0.9, position: { top: '110px', left: '40px' } },
    { text: "Awesome! 😊", isSent: false, delay: 1.2, position: { top: '140px', left: '10px' } },
  ];

  const hearts = [
    { delay: 0, position: { top: '10%', left: '5%', size: '20px' } },
    { delay: 1, position: { top: '80%', left: '85%', size: '16px' } },
    { delay: 2, position: { top: '20%', left: '90%', size: '24px' } },
    { delay: 3, position: { top: '70%', left: '10%', size: '18px' } },
  ];

  const particles = [
    { delay: 0, position: { top: '15%', left: '20%' } },
    { delay: 0.5, position: { top: '60%', left: '75%' } },
    { delay: 1, position: { top: '30%', left: '80%' } },
    { delay: 1.5, position: { top: '75%', left: '25%' } },
    { delay: 2, position: { top: '45%', left: '15%' } },
    { delay: 2.5, position: { top: '25%', left: '70%' } },
  ];

  return (
    <SceneContainer>
      <GlowEffect />
      
      {/* Floating particles */}
      {particles.map((particle, i) => (
        <Particle
          key={`particle-${i}`}
          $delay={particle.delay}
          $position={particle.position}
        />
      ))}
      
      {/* Floating hearts */}
      {hearts.map((heart, i) => (
        <FloatingHeart
          key={`heart-${i}`}
          $delay={heart.delay}
          $position={heart.position}
        >
          ❤️
        </FloatingHeart>
      ))}
      
      {/* Phone mockup */}
      <PhoneMockup>
        <PhoneScreen>
          {chatMessages.map((msg, i) => (
            <ChatBubble
              key={`bubble-${i}`}
              $isSent={msg.isSent}
              $delay={msg.delay}
              $position={msg.position}
            >
              {msg.text}
            </ChatBubble>
          ))}
        </PhoneScreen>
      </PhoneMockup>
    </SceneContainer>
  );
};

